import { Button, SvgIcon, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RightDrawer from "shared/ui/Drawer";
import { ITemplate } from "store/interfaces/campaignDetails";
import {
  DeleteCustomTemplate,
  GetTemplates,
  selectTemplates,
} from "store/slicers/campaignDetail";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import AddEditTemplate from "./AddEditTemplate";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import { ERequestStatus } from "store/enums/index.enum";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useTranslation from "shared/helpers/hooks/useTranslation";
import { defaultFilterValues } from "resources/constants";
import { useForm } from "react-hook-form";
import BasicTable from "shared/ui/Table";
import { templateColumns } from "./constants";
import { selectTableLoadingState, setTableLoading } from "store/slicers/common";
import TemplateViewTypes, { ETemplateViewTypes } from "./TemplateViewTypes";
import TemplateCardList from "./TempalteCardList";

const Templates = () => {
  const { id } = useParams();
  const [templateViewVisibilityType, setTemplateViewVisibilityType] = useState(
    ETemplateViewTypes.Card
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTemplateData, setEditTemplateData] = useState<ITemplate | null>(
    null
  );

  const loading = useSelector(selectTableLoadingState);

  const methods = useForm({
    defaultValues: {
      config: { ...defaultFilterValues, filters: [], length: 8 },
    },
  });

  const { getValues } = methods;

  const t = useTranslation();

  const templateList = useSelector(selectTemplates);
  const dispatch = useAsyncDispatch();
  
  const fetchTemplates = useCallback(async () => {
    dispatch(setTableLoading(true));
    await dispatch(
      GetTemplates({ formData: getValues("config"), id: Number(id) })
    );
    dispatch(setTableLoading(false));
  }, [dispatch, getValues, id]);

  const editTemplate = (templateData: ITemplate) => {
    setEditTemplateData(templateData);
    setDrawerOpen(true);
  };

  const handleSuccess = () => {
    setDrawerOpen(false);
    setEditTemplateData(null);
  };

  const deleteTemplate = useCallback(
    async (rowData: ITemplate) => {
      const { meta } = await dispatch(DeleteCustomTemplate(rowData.id));
      if (meta.requestStatus !== ERequestStatus.FULFILLED) {
        return;
      }

      await dispatch(
        GetTemplates({ formData: getValues("config"), id: Number(id) })
      );
      toast.success("Template removed successfully");
    },
    [dispatch, id, getValues]
  );

  const getActions = useCallback(
    (rowData: ITemplate) => {
      return [
        {
          label: "edit",
          onClick: () => editTemplate(rowData),
        },
        { label: "delete", onClick: () => deleteTemplate(rowData) },
      ];
    },
    [deleteTemplate]
  );

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTemplates]);

  return (
    <Box p={2} mx={2}>
      <Box
        my={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <TemplateViewTypes
          type={templateViewVisibilityType}
          setType={setTemplateViewVisibilityType}
        />
        <Button
          variant="outlined"
          startIcon={
            <SvgIcon>
              <PlusIcon />
            </SvgIcon>
          }
          onClick={() => setDrawerOpen(true)}
        >
          <Typography>{t("add")}</Typography>
        </Button>
      </Box>

      <Box>
        {templateViewVisibilityType === ETemplateViewTypes.Grid ? (
          <BasicTable<ITemplate>
            hasSearchInput
            filterOptions={{ watch: methods.watch, reset: methods.reset }}
            columns={templateColumns}
            getActions={getActions}
            paginatedData={templateList}
            onChange={fetchTemplates}
          />
        ) : (
          <TemplateCardList
            filterOptions={{ watch: methods.watch, reset: methods.reset }}
            list={templateList}
            actions={getActions}
            onChange={fetchTemplates}
            isLoading={loading}
          />
        )}
      </Box>

      <RightDrawer
        width={400}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        title={"Add theme"}
      >
        <AddEditTemplate
          onSuccess={handleSuccess}
          editData={editTemplateData}
        />
      </RightDrawer>
    </Box>
  );
};

export default Templates;
